import type { CSSProperties, RefObject } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const SETTINGS_OUTLET_SCROLL_MIN_HEIGHT_PX = 200;
const SETTINGS_OUTLET_SCROLL_BOTTOM_GUTTER_PX = 0;

export interface UseSettingsOutletScrollArgs {
  enabled: boolean;
}

export interface UseSettingsOutletScrollResult {
  bodyScrollHostRef: RefObject<HTMLDivElement>;
  bodyScrollHostStyle: CSSProperties | undefined;
}

export const useSettingsOutletScroll = ({
  enabled,
}: UseSettingsOutletScrollArgs): UseSettingsOutletScrollResult => {
  const bodyScrollHostRef = useRef<HTMLDivElement>(null);
  const scrollHeightRafRef = useRef<number | undefined>(undefined);
  const [scrollHostHeightPx, setScrollHostHeightPx] = useState<number | null>(
    null,
  );

  const updateScrollHostHeight = useCallback(() => {
    const el = bodyScrollHostRef.current;
    if (!el) {
      return;
    }
    const top = el.getBoundingClientRect().top;
    const vv = window.visualViewport;
    const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const next = Math.max(
      SETTINGS_OUTLET_SCROLL_MIN_HEIGHT_PX,
      Math.floor(
        viewportBottom - top - SETTINGS_OUTLET_SCROLL_BOTTOM_GUTTER_PX,
      ),
    );
    setScrollHostHeightPx(next);
  }, []);

  const scheduleScrollHostHeight = useCallback(() => {
    if (scrollHeightRafRef.current != null) {
      return;
    }
    scrollHeightRafRef.current = window.requestAnimationFrame(() => {
      scrollHeightRafRef.current = undefined;
      updateScrollHostHeight();
    });
  }, [updateScrollHostHeight]);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    updateScrollHostHeight();
    window.addEventListener('resize', updateScrollHostHeight);
    window.addEventListener('scroll', scheduleScrollHostHeight, true);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', updateScrollHostHeight);
    vv?.addEventListener('scroll', scheduleScrollHostHeight);
    return () => {
      window.removeEventListener('resize', updateScrollHostHeight);
      window.removeEventListener('scroll', scheduleScrollHostHeight, true);
      vv?.removeEventListener('resize', updateScrollHostHeight);
      vv?.removeEventListener('scroll', scheduleScrollHostHeight);
      if (scrollHeightRafRef.current != null) {
        window.cancelAnimationFrame(scrollHeightRafRef.current);
        scrollHeightRafRef.current = undefined;
      }
    };
  }, [enabled, scheduleScrollHostHeight, updateScrollHostHeight]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const html = document.documentElement;
    const { body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [enabled]);

  const bodyScrollHostStyle: CSSProperties | undefined =
    scrollHostHeightPx != null
      ? { height: scrollHostHeightPx, minHeight: 0 }
      : undefined;

  return { bodyScrollHostRef, bodyScrollHostStyle };
};
